import { ToggleSwitch, Tooltip, Box } from "@planningcenter/tapestry-react"
import pcoURL from "@planningcenter/url"
import Icon from "components/planning_center_icon"
import { Checkbox } from "components/ui"
import useFeature from "hooks/use_feature"
import useFlipperFlag from "hooks/use_flipper_flag"
import { bool, number, shape, string } from "prop-types"
import { useState } from "react"
import { apiAsync as api } from "utils/api"
import { SettingContextProvider, SettingPane, useSettings } from "./SettingPane"
import { HelperShelfMessage } from "components/helper_shelf"

const PaymentSettings = ({ event: eventFromProps, isStripeEnabled }) => {
  const availablePaymentMethods = () => {
    const methods = ["offline"]
    if (isStripeEnabled) {
      methods.push("credit")
      if (achFlipperEnabled) {
        methods.push("ach")
      }
    }
    return methods
  }

  const defaultPaymentMethod = () => {
    if (
      isStripeEnabled &&
      !eventFromProps.isOfflinePaymentEnabled &&
      (!achFlipperEnabled || (achFlipperEnabled && !eventFromProps.isACHPaymentEnabled))
    ) {
      return "credit"
    } else if (!isStripeEnabled) {
      return "offline"
    }
    return ""
  }

  const { isEditing, onCancel, onSaving, onError, onSuccess } = useSettings()
  const { enabled: unifiedSignupsEnabled } = useFeature(
    "unified_detailed_signups_ccw",
    eventFromProps.id
  )
  const achFlipperEnabled =
    useFlipperFlag("ROLLOUT_allow_ach_payments") && eventFromProps.currency == "USD"

  const [event, setEvent] = useState(eventFromProps)
  const [enableOffline, setEnableOffline] = useState(
    defaultPaymentMethod() === "offline" ? true : eventFromProps.isOfflinePaymentEnabled
  )
  const [enableCredit, setEnableCredit] = useState(
    availablePaymentMethods().includes("credit") &&
      (defaultPaymentMethod() === "credit" || eventFromProps.isCreditCardPaymentEnabled)
  )

  const [enableACH, setEnableACH] = useState(
    availablePaymentMethods().includes("ach") ? eventFromProps.isACHPaymentEnabled : false
  )

  const handleCancel = () => {
    setEnableOffline(
      defaultPaymentMethod() === "offline" ? true : eventFromProps.isOfflinePaymentEnabled
    )
    setEnableCredit(
      availablePaymentMethods().includes("credit") &&
        (defaultPaymentMethod() === "credit" || eventFromProps.isCreditCardPaymentEnabled)
    )
    setEnableACH(
      availablePaymentMethods().includes("ach") ? eventFromProps.isACHPaymentEnabled : false
    )
    onCancel()
  }

  const handleSave = async () => {
    onSaving(true)

    let paymentMethodAttrs = {
      credit_card_payments_enabled_at: enableCredit ? new Date() : null,
      offline_payments_enabled_at: enableOffline ? new Date() : null,
      ach_payments_enabled_at: enableACH ? new Date() : null
    }

    const response = await api.put(event.paths.show, { event: paymentMethodAttrs })

    if (response.errors) {
      onError(response.errors)
    } else {
      setEvent(response.event)
      await onSuccess()
    }
  }

  const handleCreditToggle = () => {
    if (enableCredit) setEnableACH(false)
    setEnableCredit(prev => !prev)
  }

  const handleOfflineToggle = () => setEnableOffline(prev => !prev)

  const handleACHToggle = () => setEnableACH(prev => !prev)

  if (!unifiedSignupsEnabled) return null

  const stripeWarning = () => {
    return !isStripeEnabled ? (
      <p className="mb-0">
        If you want to accept{" "}
        {achFlipperEnabled ? "credit, debit, or bank account " : "credit/debit "} payments, you need
        to set up the Stripe integration for your account.{" "}
        <a
          href={`${pcoURL(railsEnv)("accounts")}/stripe/oauth/new?from_app=Registrations&path=${
            window.location.pathname
          }`}
          target="_blank"
          rel="noreferrer"
        >
          Set up now
        </a>
      </p>
    ) : (
      ""
    )
  }

  return (
    <SettingPane
      title="Payments"
      onSave={handleSave}
      onCancel={handleCancel}
      blankState={
        event.isArchived && (
          <div>This signup is archived so it can not be registered for on Church Center.</div>
        )
      }
    >
      {!isEditing && (
        <>
          <h4 className="label">Payment methods</h4>
          {isStripeEnabled && enableCredit && <div>Credit or debit card</div>}
          {achFlipperEnabled && isStripeEnabled && enableACH && <div>Bank account</div>}
          {enableOffline && <div>Cash or check</div>}
        </>
      )}

      {isEditing && (
        <>
          <div className="d-f fd-c g-1 mb-4">
            <h4 className="label mb-2">Available payment methods</h4>
            <ToggleSwitch
              label="Online Payments"
              isChecked={enableCredit}
              disabled={!isStripeEnabled}
              onClick={handleCreditToggle}
            />
            {!isStripeEnabled && (
              <div className="d-f g-1 helper-shelf__inner mt-0">
                <Icon
                  symbol="general#outlined-info-circle"
                  className="c-neutral-600"
                  css={{ flexShrink: "0", marginTop: "3px" }}
                />
                {stripeWarning()}
              </div>
            )}
            <div className="d-f fd-c" css={{ marginLeft: "43px" }}>
              {isStripeEnabled && (
                <p css={{ fontSize: "var(--t-font-size-sm)" }}>
                  Allow payment methods such as credit cards, debit cards, and digital wallets (like
                  Apple Pay)
                  {achFlipperEnabled ? ", with the option to include bank account payments." : "."}
                </p>
              )}
              {achFlipperEnabled && enableCredit && (
                <>
                  <Checkbox
                    name={`enable_ach`}
                    htmlFor={`enable_ach`}
                    text="Allow bank account payments"
                    value={"ach"}
                    checked={enableACH}
                    onSelect={handleACHToggle}
                    disabled={!isStripeEnabled}
                  />
                  <HelperShelfMessage initState="open">
                    Bank transactions can take 1-3 days to process. If a payment fails, the
                    registration will still be completed, but the attendee may have an outstanding
                    balance that needs to be resolved.
                  </HelperShelfMessage>
                </>
              )}
            </div>
            <Tooltip
              placement="bottom-start"
              title={
                !isStripeEnabled
                  ? "To disable this, first connect a Stripe account for online payments."
                  : ""
              }
            >
              <Box>
                <ToggleSwitch
                  label="Cash or check"
                  isChecked={enableOffline}
                  disabled={!isStripeEnabled}
                  onClick={handleOfflineToggle}
                />
              </Box>
            </Tooltip>
          </div>

          {event.guestMode !== "disabled" && (
            <div className="font-size-4 mt-1 c-neutral-600">
              Require login must be enabled to accept online payments.
            </div>
          )}
        </>
      )}
    </SettingPane>
  )
}

PaymentSettings.propTypes = {
  isStripeEnabled: bool,
  event: shape({
    id: number,
    isSimple: bool,
    isArchived: bool,
    paymentMethod: string,
    paths: shape({
      show: string
    })
  })
}

const Payments = props => (
  <SettingContextProvider>
    <PaymentSettings {...props} />
  </SettingContextProvider>
)

export default Payments
